import { Injectable, OnDestroy } from '@angular/core';
import { hydra, Hydra, HydraOptions } from '@arcadia/hydra';
import { AuthService } from '@auth0/auth0-angular';
import { firstValueFrom, Subject } from 'rxjs';

import { logger } from '@assess/shared/logger';

import { HydraConfig } from './hydra.config';

@Injectable({ providedIn: 'root' })
export class HydraService implements OnDestroy {
  private hydra: Promise<Hydra>;

  private onIdleEnd = new Subject<void>();
  private onIdleStart = new Subject<void>();
  private onKeepalive = new Subject<void>();
  private onLogout = new Subject<void>();
  private onTimeout = new Subject<void>();

  readonly onIdleEnd$ = this.onIdleEnd.asObservable();
  readonly onIdleStart$ = this.onIdleStart.asObservable();

  constructor(
    private readonly authService: AuthService,
    readonly config: HydraConfig
  ) {
    const options: HydraOptions = {
      ...config,
      ...{
        getAccessToken: () => this.getAccessToken(),
        onIdleEnd: () => this.onIdleEnd.next(),
        onIdleStart: () => this.onIdleStart.next(),
        onKeepalive: () => this.handleKeepAlive(), // eslint-disable-line @typescript-eslint/no-misused-promises
        onLogout: () => this.handleLogout(),
        onTimeout: () => this.handleTimeout(),
      },
    };

    this.hydra = hydra(options);
  }

  logout(returnTo?: string) {
    this.hydra.then((r) => r.notifyLogout()).catch((e) => logger.error(e));

    this.authService.logout({ logoutParams: { returnTo: returnTo ?? window.location.origin } });
  }

  ngOnDestroy() {
    this.hydra.then((r) => r.dispose()).catch((e) => logger.error(e));

    this.onIdleEnd.complete();
    this.onIdleStart.complete();
    this.onKeepalive.complete();
    this.onLogout.complete();
    this.onTimeout.complete();
  }

  private getAccessToken() {
    return firstValueFrom(this.authService.getAccessTokenSilently()).catch((e: unknown) => {
      logger.error(e);

      return '';
    });
  }

  private async handleKeepAlive() {
    await this.getAccessToken();

    this.onKeepalive.next();
  }

  private handleLogout() {
    this.onLogout.next();

    this.logout();
  }

  private handleTimeout() {
    this.onTimeout.next();

    this.logout();
  }
}
